import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import {
  Buildings, ArrowRight, UsersThree, ShieldCheck,
  MapPin, Lightning, ShoppingCart, Storefront, WhatsappLogo,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { WHATSAPP_LINK } from "@/lib/whatsapp";
import { setFloatingStack } from "@/lib/floatingStack";
import { scaffoldingTypeBadges } from "@/lib/supplierTypeLabels";
import { loadBuyerRfqDraft } from "@/lib/buyerRfqDraft";

const STATS = [
  { value: "500+", label: "Active buyers" },
  { value: "500+", label: "Leads delivered" },
  { value: "20+", label: "Cities" },
  { value: "24hr", label: "Avg response" },
];

const STEPS = [
  { n: "01", title: "Post your requirement", desc: "Pick materials from our catalog, set quantities, add project details and timeline." },
  { n: "02", title: "Get matched instantly", desc: "Your requirement reaches verified suppliers in your city who stock what you need." },
  { n: "03", title: "Compare & choose", desc: "Receive competitive quotes, compare delivery terms, and select the best fit." },
];

const PILLARS = [
  { icon: UsersThree, title: "For buyers", desc: "Pick materials from our catalog, review everything in one enquiry, and attach project details — no spreadsheets, no endless calls." },
  { icon: ShieldCheck, title: "Verified network", desc: "Suppliers are onboarded with consistent profiles — what they supply, where they operate, and how they engage with RFQs." },
  { icon: MapPin, title: "City-aware sourcing", desc: "Requirements reflect where the work happens. Suppliers respond with logistics and lead times that match your actual site." },
  { icon: Lightning, title: "Fast path to quotes", desc: "From selecting materials to a submitted enquiry in one sitting — or save as you go. Built for how you actually work." },
];

export default function BuyerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [suppliers, setSuppliers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});

  const syncDraftProducts = useCallback(() => {
    const d = loadBuyerRfqDraft();
    const sp = d?.selectedProducts && typeof d.selectedProducts === "object" ? d.selectedProducts : {};
    setSelectedProducts(sp);
  }, []);

  useEffect(() => {
    api.get("/public/suppliers").then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setSuppliers(items);
    }).catch(() => {});
  }, []);

  useEffect(() => { syncDraftProducts(); }, [location.pathname, location.key, syncDraftProducts]);

  useEffect(() => {
    window.addEventListener("focus", syncDraftProducts);
    const onVis = () => { if (document.visibilityState === "visible") syncDraftProducts(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", syncDraftProducts);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [syncDraftProducts]);

  const selectedCount = Object.keys(selectedProducts).length;

  // WhatsApp button always sits at the bottom-right; the enquiry FAB stacks above it
  // when items are selected, and the chatbot bubble sits above both.
  useEffect(() => {
    setFloatingStack(selectedCount > 0 ? 2 : 1);
    return () => setFloatingStack(0);
  }, [selectedCount]);

  const cartBadgeTotal = useMemo(
    () => Object.values(selectedProducts).reduce((sum, p) => {
      if (p.specType === "area") return sum + 1;
      const q = Math.floor(Number(p.quantity));
      return sum + (Number.isFinite(q) && q > 0 ? q : 0);
    }, 0),
    [selectedProducts]
  );

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EF" }}>
      <BuyerPublicNavbar />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
        style={{ background: "#F7F4EF" }}
      >
        {/* Top orange rule */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF7A00]" />

        {/* Large decorative background word */}
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 font-black leading-none select-none pointer-events-none hidden lg:block"
          style={{
            fontSize: "clamp(14rem, 22vw, 22rem)",
            color: "rgba(0,0,0,0.03)",
            letterSpacing: "-0.06em",
            lineHeight: 1,
            right: "-4rem",
          }}
        >
          RFQ
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-8 lg:gap-16 items-center py-16 sm:py-20 lg:py-24">

            {/* Left — headline + CTA */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="h-px flex-1 max-w-[48px]" style={{ background: "#FF7A00" }} />
                <p className="text-[10px] font-black tracking-[0.25em] uppercase" style={{ color: "#FF7A00" }}>
                  India's Scaffolding Marketplace
                </p>
              </div>
              <h1
                className="font-black text-neutral-900 leading-none tracking-tighter mb-6"
                style={{ fontSize: "clamp(3rem, 7vw, 6.5rem)", letterSpacing: "-0.04em" }}
              >
                Scaffolding,<br />
                sourced<br />
                <span
                  className="relative inline-block"
                  style={{ color: "#FF7A00" }}
                >
                  smarter.
                  <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 200 6" preserveAspectRatio="none" aria-hidden>
                    <path d="M0 5 Q50 0 100 4 Q150 8 200 3" stroke="#FF7A00" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4" />
                  </svg>
                </span>
              </h1>
              <p className="text-base sm:text-lg leading-relaxed mb-10 text-neutral-500" style={{ maxWidth: "460px" }}>
                Post your scaffolding requirements and get competitive quotes from pre-verified suppliers in your city. Free. No login required.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="hero-cta-btn"
                  type="button"
                  onClick={() => navigate("/materials")}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-black transition-all hover:opacity-90 active:scale-95 shadow-lg"
                  style={{ background: "#FF7A00", color: "#fff", boxShadow: "0 8px 28px rgba(255,122,0,0.35)" }}
                >
                  Rent / Buy scaffolding <ArrowRight size={16} weight="bold" />
                </button>
                <button
                  data-testid="hero-sell-btn"
                  type="button"
                  onClick={() => navigate("/sell")}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-bold transition-all hover:bg-neutral-100"
                  style={{ background: "#fff", color: "#333", border: "1.5px solid #D8D2C8" }}
                >
                  Sell used products <ArrowRight size={16} weight="bold" />
                </button>
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-5 mt-10">
                {STATS.map((s) => (
                  <div key={s.label} className="flex items-baseline gap-1.5">
                    <span className="font-black text-neutral-900 text-lg leading-none">{s.value}</span>
                    <span className="text-xs text-neutral-400 font-medium">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — floating step cards */}
            <div className="hidden lg:flex flex-col gap-3 relative">
              {/* Vertical connector line */}
              <div
                className="absolute left-6 top-10 bottom-10 w-px"
                style={{ background: "linear-gradient(to bottom, transparent, #FF7A00 20%, #FF7A00 80%, transparent)" }}
              />
              {STEPS.map((step, i) => (
                <div
                  key={step.n}
                  className="relative rounded-2xl p-5 pl-16 transition-all"
                  style={{
                    background: i === 0 ? "#fff" : "#FDFCF9",
                    border: `1.5px solid ${i === 0 ? "#FF7A00" : "#E8E3D9"}`,
                    boxShadow: i === 0 ? "0 8px 24px rgba(255,122,0,0.12)" : "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  {/* Step dot */}
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: i === 0 ? "#FF7A00" : "#E8E3D9" }}
                  >
                    <span className="text-[9px] font-black" style={{ color: i === 0 ? "#fff" : "#aaa" }}>{i + 1}</span>
                  </div>
                  <p className="text-xs font-black text-neutral-900 mb-0.5">{step.title}</p>
                  <p className="text-xs text-neutral-400 leading-relaxed line-clamp-2">{step.desc}</p>
                </div>
              ))}
              {/* Bottom CTA hint */}
              <div
                className="rounded-2xl p-5 flex items-center justify-between mt-1"
                style={{ background: "#FF7A00" }}
              >
                <p className="text-sm font-black text-white">Ready to start?</p>
                <button
                  type="button"
                  onClick={() => navigate("/materials")}
                  className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full transition-all hover:opacity-90"
                  style={{ background: "#fff", color: "#FF7A00" }}
                >
                  Post requirement <ArrowRight size={12} weight="bold" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────── */}
      <div style={{ background: "#F0EBE3" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 sm:py-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
                How it works
              </p>
              <h2
                className="font-black text-neutral-900 leading-tight tracking-tighter"
                style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)" }}
              >
                Three steps to your first quote.
              </h2>
            </div>
            <button
              type="button"
              onClick={() => navigate("/materials")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold shrink-0 transition-all hover:opacity-90"
              style={{ background: "#FF7A00", color: "#fff" }}
            >
              Start now <ArrowRight size={14} weight="bold" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {STEPS.map((step, i) => (
              <div
                key={step.n}
                className="relative rounded-2xl p-7"
                style={{ background: "#fff", border: "1px solid #E8E3D9" }}
              >
                <p
                  className="font-black leading-none mb-5"
                  style={{ fontSize: "3.5rem", color: "rgba(255,122,0,0.12)", letterSpacing: "-0.05em" }}
                >
                  {step.n}
                </p>
                {i < STEPS.length - 1 && (
                  <div className="hidden sm:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 w-6 h-6 rounded-full items-center justify-center"
                    style={{ background: "#FF7A00" }}>
                    <ArrowRight size={12} weight="bold" className="text-white" />
                  </div>
                )}
                <h3 className="font-black text-neutral-900 mb-2 leading-snug text-base">{step.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ABOUT / PILLARS ───────────────────────────────────── */}
      <section id="about" data-testid="about-section" style={{ background: "#F7F4EF" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-20 mb-10">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
                About Scaffoindia
              </p>
              <div className="w-10 h-1 rounded-full mb-5" style={{ background: "#FF7A00" }} />
            </div>
            <div>
              <h2
                className="font-black text-neutral-900 leading-tight tracking-tighter mb-4"
                style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)" }}
              >
                Built for scaffolding buyers and suppliers.
              </h2>
              <p className="text-neutral-500 text-sm sm:text-base leading-relaxed">
                Scaffoindia is a dedicated marketplace for scaffolding, shuttering, and related construction equipment. We help project teams capture what they need with a structured catalog, then route requirements to suppliers who are set up to quote quickly and clearly — whether you are renting for a short job or buying material outright.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {PILLARS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="rounded-2xl p-7 flex gap-5"
                style={{ background: "#fff", border: "1px solid #E8E3D9" }}
              >
                <div
                  className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center mt-0.5"
                  style={{ background: "rgba(255,122,0,0.1)" }}
                >
                  <Icon size={20} weight="bold" style={{ color: "#FF7A00" }} />
                </div>
                <div>
                  <h3 className="font-black text-neutral-900 mb-2 text-sm">{title}</h3>
                  <p className="text-sm text-neutral-500 leading-relaxed">
                    {title === "Fast path to quotes" ? (
                      <>
                        Start from{" "}
                        <button type="button" onClick={() => navigate("/materials")}
                          className="font-semibold underline-offset-2 hover:underline" style={{ color: "#FF7A00" }}>
                          Select materials
                        </button>{" "}
                        to build your list, then continue to your requirement when ready. The flow is designed to be finished in one sitting or saved as you go.
                      </>
                    ) : desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SUPPLIERS ─────────────────────────────────────────── */}
      {suppliers.length > 0 && (
        <section data-testid="suppliers-section" style={{ background: "#1a1a1a" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 sm:py-16">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
                  Verified Network
                </p>
                <h2
                  className="font-black text-white leading-tight tracking-tighter"
                  style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)" }}
                >
                  Our supplier partners.
                </h2>
              </div>
              <Link
                to="/suppliers"
                data-testid="view-all-suppliers-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold shrink-0 transition-all hover:opacity-80"
                style={{ background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.12)" }}
              >
                View all suppliers <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {suppliers.slice(0, 6).map((s, i) => {
                const typeBadges = scaffoldingTypeBadges(s.supplier_type);
                return (
                  <div
                    key={s.id || i}
                    className="rounded-2xl p-6 flex flex-col gap-4"
                    style={{ background: "#222", border: "1px solid #2e2e2e" }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: "rgba(255,122,0,0.12)" }}
                      >
                        <Buildings size={18} weight="bold" style={{ color: "#FF7A00" }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm text-white leading-snug truncate">
                          {s.name || s.company_name || "Supplier"}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {typeBadges.map((label) => (
                            <span
                              key={label}
                              className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                              style={{ background: "rgba(255,122,0,0.15)", color: "#FF7A00" }}
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {(s.cities_served || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {(s.cities_served || []).map((c) => (
                          <span
                            key={c}
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                            style={{ background: "#2e2e2e", color: "#888" }}
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── DUAL CTA ──────────────────────────────────────────── */}
      <div style={{ background: "#F0EBE3" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {/* Buyers */}
            <div
              className="rounded-2xl p-8 sm:p-10 flex flex-col"
              style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2d1a08 100%)", border: "1px solid #2a2a2a" }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: "rgba(255,122,0,0.15)" }}>
                <UsersThree size={22} weight="bold" style={{ color: "#FF7A00" }} />
              </div>
              <h3 className="text-xl font-black text-white mb-3">For buyers</h3>
              <p className="text-sm leading-relaxed flex-1" style={{ color: "#999" }}>
                Post scaffolding requirements and receive competitive quotes from pre-verified suppliers. Free, no login required.
              </p>
              <button
                type="button"
                onClick={() => navigate("/materials")}
                className="inline-flex items-center gap-2 mt-8 text-sm font-bold transition-opacity hover:opacity-80 w-fit"
                style={{ color: "#FF7A00" }}
              >
                Browse materials <ArrowRight size={14} weight="bold" />
              </button>
            </div>

            {/* Suppliers */}
            <div
              className="rounded-2xl p-8 sm:p-10 flex flex-col"
              style={{ background: "#fff", border: "1px solid #E8E3D9" }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: "rgba(255,122,0,0.1)" }}>
                <Storefront size={22} weight="bold" style={{ color: "#FF7A00" }} />
              </div>
              <h3 className="text-xl font-black text-neutral-900 mb-3">For suppliers</h3>
              <p className="text-sm text-neutral-500 leading-relaxed flex-1">
                Grow your scaffolding business with quality leads from buyers who have structured, actionable requirements. Sign up and start receiving RFQs.
              </p>
              <Link
                to="/supplier/login"
                className="inline-flex items-center gap-2 mt-8 text-sm font-bold transition-opacity hover:opacity-80 w-fit"
                style={{ color: "#FF7A00" }}
              >
                Supplier registration <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── ENQUIRY RESUME BANNER ────────────────────────────── */}
      {selectedCount > 0 && (
        <section className="py-5" style={{ background: "#FFF7F0" }} aria-label="Continue enquiry">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
            <div
              className="rounded-2xl px-5 py-4 flex items-center justify-between gap-4"
              style={{ background: "rgba(255,122,0,0.08)", border: "1px solid rgba(255,122,0,0.2)" }}
            >
              <p className="text-sm text-neutral-800">
                <span className="font-bold" style={{ color: "#FF7A00" }}>Next:</span>{" "}
                open <span className="font-semibold">View Enquiry</span> to review lines, fill project details, and submit your requirement.
              </p>
              <button
                type="button"
                onClick={() => navigate("/requirement")}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full transition-all hover:opacity-90"
                style={{ background: "#FF7A00", color: "#fff" }}
              >
                View Enquiry <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── VIEW ENQUIRY FAB ─────────────────────────────────── */}
      {selectedCount > 0 && (
        <button
          type="button"
          data-testid="home-view-enquiry-fab"
          onClick={() => navigate("/requirement")}
          className="fixed bottom-28 right-6 z-40 inline-flex items-center gap-3 pl-5 pr-2 py-2.5 rounded-full text-white text-sm font-bold shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: "#FF7A00", border: "1px solid #e86800", boxShadow: "0 4px 20px rgba(255,122,0,0.35)" }}
        >
          <ShoppingCart size={22} weight="bold" className="shrink-0" aria-hidden />
          <span>View Enquiry</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-extrabold tabular-nums" style={{ color: "#FF7A00" }}>
            {cartBadgeTotal}
          </span>
        </button>
      )}

      {/* Floating WhatsApp */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        data-testid="home-whatsapp-fab"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all hover:scale-110 active:scale-95"
        style={{ background: "#25D366", color: "#fff", boxShadow: "0 4px 20px rgba(37,211,102,0.4)" }}
      >
        <WhatsappLogo size={28} weight="fill" />
      </a>

      <Footer />
    </div>
  );
}
